'use client';

import classNames from 'classnames';
import { type FC, type FormHTMLAttributes, type PropsWithChildren, type Ref, useActionState } from 'react';

import styles from './form-box.module.scss';

type FormBoxProps = Omit<FormHTMLAttributes<HTMLFormElement>, 'action'> &
  PropsWithChildren & {
    action: (state: undefined, formData: FormData) => Promise<undefined>;
    ref?: Ref<HTMLFormElement>;
    wide?: boolean;
  };

export const FormBox: FC<FormBoxProps> = ({ action, children, className, ref, wide, ...props }) => {
  const [, dispatch] = useActionState(action, undefined);

  return (
    <form
      className={classNames(styles.box, wide && styles['box-wide'], className)}
      {...props}
      action={dispatch}
      ref={ref}
    >
      {children}
    </form>
  );
};

type RawFormBoxProps = Omit<FormHTMLAttributes<HTMLFormElement>, 'action'> &
  PropsWithChildren & { dispatch: (formData: FormData) => void; wide?: boolean };

export const RawFormBox: FC<RawFormBoxProps> = ({ dispatch, children, className, wide, ...props }) => {
  return (
    <form className={classNames(styles.box, wide && styles['box-wide'], className)} {...props} action={dispatch}>
      {children}
    </form>
  );
};

type ContentBoxProps = PropsWithChildren & { wide?: boolean };

export const ContentBox: FC<ContentBoxProps> = ({ children, wide }) => (
  <div className={classNames(styles.box, wide && styles['box-wide'])}>{children}</div>
);

export const Fieldset: FC<PropsWithChildren> = ({ children }) => (
  <fieldset className={styles.fieldset}>{children}</fieldset>
);

type FormRowProps = PropsWithChildren & {
  desktopColumns: '1fr' | '1fr-1fr' | '2fr-1fr' | '1fr-2fr';
};

export const FormRow: FC<FormRowProps> = ({ children, desktopColumns }) => (
  <div className={classNames(styles.row, styles[`row-columns-${desktopColumns}`])}>{children}</div>
);
